/**
 * One checklist row — same chrome as launch-readiness `MilestoneRow`: status
 * icon + title + detail + exactly one outlined action (contained for the
 * first open row), or a "Retry" button when the evaluator threw (checkFailed,
 * AC-056) instead of the normal action.
 */
import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import type { ActivationItem } from '@modules/shared-types';
import { LAUNCH_ACTIVATION_TEST_IDS } from '../testIds';
import { VendorPath } from './VendorPath';

export interface ActivationItemRowProps {
  item: ActivationItem;
  /** The first open row in list order gets the contained (primary) button. */
  primary?: boolean;
  onAction: (item: ActivationItem) => void;
  onRetry: () => void;
}

/** earned = check; checkFailed = "?" (the evaluator couldn't tell); open = "!" (missing), matching MilestoneRow's status icons. */
function RowStatusIcon({ item }: { item: ActivationItem }) {
  if (item.earned) return <CheckCircleIcon color="success" fontSize="small" />;
  if (item.checkFailed) return <HelpOutlineIcon color="disabled" fontSize="small" aria-label="Check unavailable" />;
  return <ErrorIcon color="warning" fontSize="small" />;
}

/**
 * Fix round 1 (controller ruling): the inline vendor click-path is class-agnostic —
 * any OPEN row whose action carries a non-empty `vendorPath` shows it (the ads-group
 * pixel rows are class D with both `vendorPath` and `articleAlias`; it's not gated on
 * a `class: 'X'` that no requirement currently uses).
 */
export const ActivationItemRow: React.FC<ActivationItemRowProps> = ({ item, primary, onAction, onRetry }) => {
  const vendorPathSteps = !item.earned && !item.checkFailed ? item.action?.vendorPath : undefined;
  return (
    <Box
      data-testid={LAUNCH_ACTIVATION_TEST_IDS.item(item.id)}
      sx={{
        px: 2.5,
        py: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <RowStatusIcon item={item} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body1">{item.title}</Typography>
          <Typography variant="body2" color="text.secondary">{item.detail}</Typography>
        </Box>
        {!item.earned && item.checkFailed && (
          <Button
            data-testid={LAUNCH_ACTIVATION_TEST_IDS.itemRetry(item.id)}
            variant="outlined"
            size="small"
            sx={{ whiteSpace: 'nowrap', flex: 'none' }}
            onClick={onRetry}
          >
            Retry
          </Button>
        )}
        {!item.earned && !item.checkFailed && item.action && (
          <Button
            data-testid={LAUNCH_ACTIVATION_TEST_IDS.itemAction(item.id)}
            variant={primary ? 'contained' : 'outlined'}
            size="small"
            sx={{ whiteSpace: 'nowrap', flex: 'none' }}
            onClick={() => onAction(item)}
          >
            {item.action.label}
          </Button>
        )}
      </Box>
      {vendorPathSteps && vendorPathSteps.length > 0 && (
        <VendorPath steps={vendorPathSteps} data-testid={LAUNCH_ACTIVATION_TEST_IDS.vendorPath(item.id)} />
      )}
    </Box>
  );
};
